import axios from 'axios';
import router from '@/router';
import store from '@/store';
import $msg from '@/utils/message';
import i18n from '@/assets/lang/index';
const t = i18n.global.t;

const PUBLIC_HASH_RE =
	/^#\/(login|wxLogin|reset|register|license|m\/product|mate\/(home|shop|product|login)(\/.*)?)(\/.*)?(\?.*)?$/;

/** 当前是否在无需登录的页面（路由 meta + hash 双保险，避免刷新瞬间 meta 未就绪） */
const isPublicLocation = () => {
	const hash = (window.location.hash || '').split('?')[0];
	if (PUBLIC_HASH_RE.test(hash)) {
		return true;
	}
	const route = router.currentRoute.value;
	if (route?.meta?.public) {
		return true;
	}
	const path = route?.path || '';
	const publicPrefixes = [
		'/login',
		'/wxLogin',
		'/reset',
		'/register',
		'/license',
		'/m/product',
		'/mate/home',
		'/mate/shop',
		'/mate/product',
		'/mate/login',
	];
	return publicPrefixes.some(
		(p) => path === p || path.startsWith(p + '/')
	);
};

const isAnonUrl = (url?: string) =>
	!!url && (url.includes('/api/anon/') || url.includes('/anon/'));

const isAuthError = (status: any, message?: any) => {
	const code = Number(status);
	const msg = typeof message === 'string' ? message : message?.message;
	return (
		code === 401 ||
		code === -1 ||
		msg === '未登录' ||
		(typeof msg === 'string' && msg.indexOf('登录') !== -1)
	);
};

const isMateLocation = () => {
	const path = router.currentRoute.value?.path || '';
	const hash = (window.location.hash || '').split('?')[0];
	return path.startsWith('/mate') || hash.startsWith('#/mate');
};

/**
 * 跳转登录页（mate 内跳 mate 登录并带回跳）
 */
const toLogin = () => {
	if (isPublicLocation()) {
		return;
	}
	if (isMateLocation()) {
		const fullPath = router.currentRoute.value?.fullPath || '/mate/home';
		router.replace({
			path: '/mate/login',
			query: { redirect: fullPath },
		});
		return;
	}
	router.replace({
		name: 'login'
	});
};

/**
 * 请求失败后的错误统一处理
 */
const errorHandle = (status: any, other: any, url?: string) => {
	const message = typeof other === 'string' ? other : other?.message;
	// 公开页 / 匿名接口：静默忽略未登录类错误
	if (
		isAuthError(status, message) &&
		(isPublicLocation() || isAnonUrl(url))
	) {
		return;
	}
	const code = Number(status);
	switch (code) {
		case -1:
			toLogin();
			break;
		case 401:
			$msg.$message(t('exception.401'), 'error');
			localStorage.removeItem('token');
			store.commit('delToken');
			store.commit('delAuth');
			setTimeout(() => {
				toLogin();
			}, 1000);
			break;
		case 403:
			$msg.$message(t('exception.403'), 'error');
			break;
		case 404:
			$msg.$message(t('exception.404'), 'error');
			break;
		default:
			$msg.$message(message || t('exception.unknown'), 'error');
	}
};

/* 实例化请求配置 */
const instance = axios.create({
	headers: {
		'Content-Type': 'application/x-www-form-urlencoded'
	},
	// 请求时长
	timeout: 1000 * 600,
	withCredentials: true
});

/**
 * 请求拦截器
 * 每次请求前，如果存在token则在请求头中携带token
 * 匿名接口不带 token / cookie，避免微信等环境因无效登录态被拒
 */
instance.interceptors.request.use(
	config => {
		const url = config.url || '';
		if (isAnonUrl(url)) {
			config.withCredentials = false;
			if (config.headers) {
				delete (config.headers as any).Authorization;
			}
			return config;
		}
		const token = store.state.token;
		if (token && token !== 'null' && token !== 'undefined') {
			localStorage.setItem('token', '' + token);
			config.headers.Authorization = token;
		}
		return config;
	}
);

// 响应拦截器
instance.interceptors.response.use(
	// 请求成功
	res => {
		const url = res.config?.url;
		if (res.status !== 200) {
			if (!(isPublicLocation() || isAnonUrl(url))) {
				$msg.error(res.data?.message);
			}
			return Promise.reject(res);
		}
		if (res.data && res.data.success) {
			return Promise.resolve(res.data.data);
		} else {
			errorHandle(res.data?.code, res.data?.message, url);
			return Promise.reject(res);
		}
	},
	// 请求失败
	error => {
		const { response, config } = error;
		if (response) {
			errorHandle(
				response.data?.code ?? response.status,
				response.data?.message ?? response.data,
				config?.url
			);
			return Promise.reject(response);
		}
		// 断网/超时等：必须 reject，否则业务侧会拿到 undefined
		return Promise.reject(error);
	}
);

export default instance;
